import { useEffect, useState, useRef } from 'react'
import { Autocomplete, FormControl, InputLabel, TextField } from '@mui/material'
import { getObjectsWithFilter } from '../../api/apiClientCrud'

function FreeSelectField({ field, isTable, optionsAPI, onChangeF, dataId, isError, editRow, onRowDataChange }) {
  const [select, setSelect] = useState([])
  const [inputText, setInputText] = useState('')
  const [value, setValue] = useState(null)
  const generateId = () => Math.floor(Math.random() * 2147483647)

  // Track the row identity (id or id_tmp) to detect when we switch to a different row
  const rowIdRef = useRef(null)
  // Track whether we are currently in a user-typing session (avoid useEffect reset)
  const isTypingRef = useRef(false)

  useEffect(() => {
    let isMounted = true
    const fetchOptions = async () => {
      try {
        const res = await getObjectsWithFilter(optionsAPI.api(editRow), optionsAPI.filter())
        if (!isMounted) return
        setSelect(res.data[optionsAPI.api(editRow)] ?? res.data)
      } catch (e) {}
    }
    fetchOptions()
    return () => {
      isMounted = false
    }
  }, [editRow?.[optionsAPI.trigger]])

  useEffect(() => {
    if (!editRow) return

    // Compute current row identity
    const currentRowId = editRow.id ?? editRow.id_tmp ?? null
    const isNewRow = currentRowId !== rowIdRef.current

    // Only sync inputText/value when we switch to a different row (mount or row change),
    // NOT on every keystroke (which also updates editRow via onRowDataChange).
    if (!isNewRow && isTypingRef.current) return

    if (isNewRow) {
      rowIdRef.current = currentRowId
      isTypingRef.current = false
    }

    if (editRow[dataId(editRow)] != null) {
      const current = select.find((item) => item[dataId(editRow)] === editRow[dataId(editRow)])
      if (current) {
        setValue(current)
        setInputText(current[field.name])
        return
      }
    }

    if (editRow[field.name]) {
      const existing = select.find((item) => item[field.name] === editRow[field.name])
      if (!existing) {
        const newId = generateId()
        const newItem = { id: newId, [dataId(editRow)]: newId, [field.name]: editRow[field.name] }
        setSelect((prev) => [...prev, newItem])
        setValue(newItem)
        setInputText(editRow[field.name])
        onRowDataChange({ ...editRow, [dataId(editRow)]: newId })
      } else {
        setValue(existing)
        setInputText(existing[field.name])
      }
    } else {
      setValue(null)
      setInputText('')
    }
  }, [editRow, select])

  const handleChange = (event, selectedValue) => {
    if (!editRow) return
    const data = { ...editRow }
    if (selectedValue) {
      data[dataId(editRow)] = selectedValue.id
      data[field.name] = selectedValue[field.name]
      setValue(selectedValue)
      setInputText(selectedValue[field.name])
    } else {
      data[dataId(editRow)] = null
      data[field.name] = null
      setValue(null)
      setInputText('')
    }
    onRowDataChange?.(data)
    onChangeF?.(data, select, onRowDataChange)
  }

  return (
    <FormControl sx={{ my: 1, width: '100%', minWidth: 0 }}>
      {(editRow?.[field.name] == null || editRow?.[field.name] === '') && !isTable && (
        <InputLabel sx={{ position: 'absolute', mt: 0.5, color: '#A4A2A2' }}>{field.label}</InputLabel>
      )}

      <Autocomplete
        options={select || []}
        value={value}
        freeSolo
        inputValue={inputText}
        onInputChange={(e, nv) => {
          if (e) {
            // User is actively typing: flag it so the useEffect doesn't override inputText
            isTypingRef.current = true
          }
          setInputText(nv)
          // CRUCIAL : On met à jour la description dans le state du tableau
          // en temps réel pendant que l'utilisateur tape.
          onRowDataChange({ ...editRow, [field.name]: nv })
        }}
        getOptionLabel={(option) => option?.[field.name] ?? ''}
        isOptionEqualToValue={(option, val) => {
          if (!option || !val) return false
          if (option.id != null && val.id != null) return option.id === val.id
          return option[field.name] === val[field.name]
        }}
        onChange={handleChange}
        sx={{
          // 1. Conteneur principal: Appliquer la troncature ici et forcer le flex
          '& .MuiInputBase-root': {
            minWidth: 0,
            flexShrink: 1,
            overflow: 'hidden !important', // On force la troncature sur le parent
            display: 'flex'
          }, // 2. L'élément de sélection : Forcer la contraction et l'espace

          '& .MuiSelect-select': {
            minWidth: '0 !important',
            overflow: 'hidden !important',
            textOverflow: 'ellipsis !important',
            whiteSpace: 'nowrap !important',
            flexGrow: '1 !important', // CLÉ
            width: 'auto !important', // CLÉ // La seule règle de style inline restante pour le texte sélectionné:
            paddingRight: '0 !important'
          },

          // 3. Icône: La masquer complètement ou la positionner absolument hors de la zone
          // (Si l'icône est masquée par l'overflow: hidden, on peut la masquer ou la forcer dehors)
          '& .MuiSelect-icon': {
            // La masquer si elle est coupée et que l'on privilégie la troncature
            // display: 'none !important',

            // OU la forcer à être un petit espace visible:
            position: 'relative',
            flexShrink: 0
          }, // 3. Troncature pour le mode FreeSolo (texte tapé)
          '& .MuiInputBase-input': {
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          } // 4. Retirer toutes les règles de .MuiSelect-select ici car elles seront injectées inline. // ... (MuiAutocomplete-tag inchangé)
        }}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              variant='standard'
              error={isError}
              sx={{ minWidth: 0 }}
              InputProps={{
                ...params.InputProps, // Nettoyer les styles sx qui ciblaient MuiSelect-select
                sx: { minWidth: 0 }
              }}
              inputProps={{
                ...params.inputProps, // ✅ APPLICATION DU STYLE INLINE (SPÉCIFICITÉ MAXIMALE)

                onKeyDown: (e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    e.stopPropagation()
                    e.nativeEvent?.stopImmediatePropagation()
                  }
                }
              }}
              onBlur={(e) => {
                const val = e.target.value?.trim()
                if (val && !select?.some((item) => item[field.name] === val)) {
                  const newId = generateId()
                  handleChange(null, { id: newId, [dataId(editRow)]: newId, [field.name]: val })
                  setInputText(val)
                }
              }}
            />
          )
        }}
      />
    </FormControl>
  )
}

export default FreeSelectField
